import { useCallback, useEffect, useRef, useState } from 'react';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import type { InitialDraft } from '@confluence/fabric-providers';
import { getCollabEditProvider, getDraft } from '@confluence/fabric-providers';
import { useEditPageLoadingActions, preloadEditorData } from '@confluence/load-edit-page';
import {
	fetchCollabPermissionToken,
	useNativeCollabActions,
	useNativeCollabPresenceState,
} from '@confluence/native-collab';
import { useSessionData } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';

export interface EditorDataLoaderProps {
	contentId: string;
	spaceKey: string | undefined;
	canEdit: boolean;
}
interface EditorDataLoaderOutput {
	isPreloading: boolean;
	errors: any[];
}

export const useEditorDataLoader = ({
	contentId,
	spaceKey,
	canEdit,
}: EditorDataLoaderProps): EditorDataLoaderOutput => {
	const { userId, cloudId } = useSessionData();
	const { nativeEditorComponentsPreloader } = useEditPageLoadingActions();
	const { setCollabEditProvider, setHasInitialDraft } = useNativeCollabActions();

	// presenceId is pulled from the native collab state to ensure that we have a consistent presenceId for a given contentId
	// across the collab providers that drive editor as well as presence components (telepointer and avatar colors).
	const [{ presenceId }] = useNativeCollabPresenceState();
	const presenceIdRef = useRef(presenceId);
	presenceIdRef.current = presenceId;

	const [isPreloading, setIsPreloading] = useState(true);
	const [errors, setErrors] = useState<any[]>([]);
	const latestPreloadingContentIdRef = useRef<string | null>(null);

	const getNCSCollabProvider = useCallback(
		(draft: InitialDraft | null, contentType: string, activity: PresenceActivity) => {
			try {
				const ncsProvider = getCollabEditProvider({
					contentId,
					cloudId,
					contentType,
					permissionTokenRefresh: fetchCollabPermissionToken(contentId, undefined),
					initialDraft: draft,
					isLivePage: true,
					presenceId: presenceIdRef.current,
					presenceActivity: activity,
					userId,
				});
				setCollabEditProvider(ncsProvider);
				// @ts-ignore
				const hasInitialDraft = Boolean(ncsProvider?.initialDraft);
				setHasInitialDraft(hasInitialDraft);
			} catch (error) {
				setErrors((prevErrors) => [...prevErrors, error]);
			}
		},
		[cloudId, contentId, presenceIdRef, userId, setCollabEditProvider, setHasInitialDraft],
	);

	// Preload should be called once per contentId
	useEffect(() => {
		const preload = async () => {
			try {
				const contentType = 'page';
				const activity = canEdit ? 'editor' : 'viewer';
				const getDraftPromise: Promise<InitialDraft | null> = getDraft(contentId);
				const results = await Promise.all([
					fg('confluence_live_pages_unified_query')
						? Promise.resolve() // does nothing, just keeps order of destructured results
						: preloadEditorData({
								spaceKey,
								contentId,
								userId,
								useCache: true, // use cache for preload queries
								cloudId,
								isBlog: false,
								isLivePage: true,
							}),
					nativeEditorComponentsPreloader(),
					getDraftPromise,
				]);
				const [, , draftResponse] = results;

				/**
				 * This if condition fixes issue where transitioning between different live docs too quickly cause NCS collab provider not connected
				 * The issue happens when user has already transitioned away from the page when the promise.all above finished
				 * To avoid the issue, only get a new NCS collab provider and finish preloading if the latest preloading content id matches the preloaded content Id
				 */
				if (
					latestPreloadingContentIdRef.current === contentId ||
					!fg('confluence_frontend_live_edit_only_get_latest_ncs')
				) {
					getNCSCollabProvider(draftResponse, contentType, activity);
					setIsPreloading(false);
					latestPreloadingContentIdRef.current = null;
				}
			} catch (error) {
				setErrors((prevErrors) => [...prevErrors, error]);
			}
		};
		latestPreloadingContentIdRef.current = contentId;
		preload().catch((error) => {
			setErrors((prevErrors) => [...prevErrors, error]);
		});
		return () => {
			setIsPreloading(true);
		};
		// The dependency array should not change per page edit
		// This prevents multiple preloads for the same contentId, which can cause NCS connection issues
	}, [
		canEdit,
		cloudId,
		contentId,
		spaceKey,
		getNCSCollabProvider,
		nativeEditorComponentsPreloader,
		userId,
		latestPreloadingContentIdRef,
	]);

	return { isPreloading, errors };
};
